import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  Toolbar,
  alpha,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Warning as WarningIcon,
  MeetingRoom as RoomIcon,
  Send as SendIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';
import { ChatRoom, ChatRoomFixedTag } from '../types';

const STATUS_LABELS: Record<string, string> = {
  pending: '未対応',
  active: '対応中',
  waiting: '返信待ち',
  completed: '完了',
  temp_completed: '一時完了',
  paused: '保留',
  archived: '削除',
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'info' | 'default' | 'secondary' | 'error'> = {
  pending: 'warning',
  active: 'success',
  waiting: 'info',
  completed: 'default',
  temp_completed: 'info',
  paused: 'secondary',
  archived: 'error',
};

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

function daysSince(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

type RoomTagMap = Record<string, ChatRoomFixedTag[]>;

const RoomManagement: React.FC = () => {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // タグ
  const [fixedTagMap, setFixedTagMap] = useState<RoomTagMap>({});

  // フィルター
  const [statusFilter, setStatusFilter] = useState<string>('active_only');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [inactiveDaysFilter, setInactiveDaysFilter] = useState<number>(0);
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  // ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // 一括操作
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'completed' | 'archived'>('completed');
  const [processing, setProcessing] = useState(false);

  // 通知
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getChatRooms(1, 5000);
      if (res.success) {
        const chatRooms = res.chatRooms || [];
        setRooms(chatRooms);

        if (chatRooms.length > 0) {
          try {
            const ids = chatRooms.map(r => r.id);
            const batchSize = 200;
            let allTags: RoomTagMap = {};
            for (let i = 0; i < ids.length; i += batchSize) {
              const batch = ids.slice(i, i + batchSize);
              const tagRes = await apiClient.getBulkRoomFixedTags(batch);
              if (tagRes.success && tagRes.map) {
                allTags = { ...allTags, ...tagRes.map };
              }
            }
            setFixedTagMap(allTags);
          } catch (e) {
            console.error('固定タグ取得エラー:', e);
          }
        }
      }
    } catch (e) {
      console.error('ルーム取得エラー:', e);
      setSnackbar({ open: true, message: 'ルーム一覧の取得に失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const getRoomAllTags = useCallback((room: ChatRoom): string[] => {
    const tags: string[] = [];
    const fixedTags = fixedTagMap[room.id];
    if (fixedTags) {
      fixedTags.forEach(ft => {
        tags.push(`${ft.category_name}:${ft.option_name}`);
      });
    }
    if (room.custom_tags && Array.isArray(room.custom_tags)) {
      room.custom_tags.forEach(t => tags.push(t));
    }
    return tags;
  }, [fixedTagMap]);

  const allTagOptions = useMemo(() => {
    const tagSet = new Set<string>();
    rooms.forEach(room => {
      getRoomAllTags(room).forEach(t => tagSet.add(t));
    });
    return Array.from(tagSet).sort();
  }, [rooms, getRoomAllTags]);

  const filteredRooms = useMemo(() => {
    let list = rooms;

    if (statusFilter === 'active_only') {
      list = list.filter(r => !['completed', 'archived'].includes(r.status));
    } else if (statusFilter !== 'all') {
      list = list.filter(r => r.status === statusFilter);
    }

    if (searchKeyword.trim()) {
      const kw = searchKeyword.trim().toLowerCase();
      list = list.filter(r =>
        r.title?.toLowerCase().includes(kw) ||
        r.participant_name?.toLowerCase().includes(kw) ||
        r.participant_email?.toLowerCase().includes(kw) ||
        r.participant_phone?.includes(kw) ||
        r.room_number?.toString().includes(kw) ||
        r.project_no?.toLowerCase().includes(kw)
      );
    }

    if (inactiveDaysFilter > 0) {
      list = list.filter(r => {
        const lastActivity = r.last_message_at || r.updated_at || r.created_at;
        const days = daysSince(lastActivity);
        return days !== null && days >= inactiveDaysFilter;
      });
    }

    if (tagFilter.length > 0) {
      list = list.filter(r => {
        const roomTags = getRoomAllTags(r);
        return tagFilter.every(ft => roomTags.includes(ft));
      });
    }

    list.sort((a, b) => {
      const aDate = a.last_message_at || a.updated_at || a.created_at;
      const bDate = b.last_message_at || b.updated_at || b.created_at;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });

    return list;
  }, [rooms, statusFilter, searchKeyword, inactiveDaysFilter, tagFilter, getRoomAllTags]);

  const paginatedRooms = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRooms.slice(start, start + rowsPerPage);
  }, [filteredRooms, page, rowsPerPage]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(new Set(paginatedRooms.map(r => r.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkConfirm = () => {
    if (selected.size === 0) {
      setSnackbar({ open: true, message: 'ルームを選択してください', severity: 'info' });
      return;
    }
    setBulkDialogOpen(true);
  };

  const handleBulkExecute = async () => {
    setProcessing(true);
    try {
      const res = await apiClient.bulkUpdateRoomStatus(Array.from(selected), bulkAction);
      if (res.success) {
        setSnackbar({ open: true, message: res.message || '一括変更完了', severity: 'success' });
        setSelected(new Set());
        await fetchRooms();
      } else {
        setSnackbar({ open: true, message: res.message || '一括変更に失敗しました', severity: 'error' });
      }
    } catch (e) {
      console.error('一括操作エラー:', e);
      setSnackbar({ open: true, message: '一括操作中にエラーが発生しました', severity: 'error' });
    } finally {
      setProcessing(false);
      setBulkDialogOpen(false);
    }
  };

  const allChecked = paginatedRooms.length > 0 && paginatedRooms.every(r => selected.has(r.id));
  const someChecked = paginatedRooms.some(r => selected.has(r.id));

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RoomIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight="bold">ルーム管理</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            表示中: {filteredRooms.length}件 / 全{rooms.length}件 | 選択中: {selected.size}件
          </Typography>
        </Box>

        {/* フィルターバー */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterIcon color="action" />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={statusFilter}
                label="ステータス"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); setSelected(new Set()); }}
              >
                <MenuItem value="active_only">アクティブのみ</MenuItem>
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="pending">未対応</MenuItem>
                <MenuItem value="active">対応中</MenuItem>
                <MenuItem value="waiting">返信待ち</MenuItem>
                <MenuItem value="paused">保留</MenuItem>
                <MenuItem value="temp_completed">一時完了</MenuItem>
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="archived">削除</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="ルーム名・顧客名・電話番号で検索"
              value={searchKeyword}
              onChange={(e) => { setSearchKeyword(e.target.value); setPage(0); }}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>未活動日数フィルター</InputLabel>
              <Select
                value={inactiveDaysFilter}
                label="未活動日数フィルター"
                onChange={(e) => { setInactiveDaysFilter(Number(e.target.value)); setPage(0); setSelected(new Set()); }}
              >
                <MenuItem value={0}>指定なし</MenuItem>
                <MenuItem value={3}>3日以上</MenuItem>
                <MenuItem value={7}>7日以上</MenuItem>
                <MenuItem value={14}>14日以上</MenuItem>
                <MenuItem value={30}>30日以上</MenuItem>
                <MenuItem value={60}>60日以上</MenuItem>
                <MenuItem value={90}>90日以上</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* タグフィルター（AND検索） */}
          {allTagOptions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
              <TagIcon color="action" fontSize="small" />
              <Autocomplete
                multiple
                size="small"
                options={allTagOptions}
                value={tagFilter}
                onChange={(_, newVal) => { setTagFilter(newVal); setPage(0); setSelected(new Set()); }}
                renderInput={(params) => (
                  <TextField {...params} placeholder={tagFilter.length === 0 ? 'タグで絞り込み（AND検索）' : ''} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
                sx={{ flex: 1, minWidth: 300 }}
                noOptionsText="タグが見つかりません"
              />
            </Box>
          )}
        </Paper>

        {/* 一括操作ツールバー */}
        {selected.size > 0 && (
          <Toolbar
            sx={{
              mb: 2,
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <Typography color="primary" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
              {selected.size}件 選択中を
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value as 'completed' | 'archived')}
                sx={{
                  fontWeight: 'bold',
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 0.8 },
                }}
              >
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="archived">削除</MenuItem>
              </Select>
            </FormControl>
            <Typography color="primary" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
              にする
            </Typography>
            <Button
              variant="contained"
              color={bulkAction === 'completed' ? 'success' : 'error'}
              startIcon={<SendIcon />}
              onClick={handleBulkConfirm}
              disabled={processing}
              sx={{ ml: 1 }}
            >
              実行
            </Button>
          </Toolbar>
        )}

        {/* ルーム一覧テーブル */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity="info" sx={{ justifyContent: 'center' }}>
                条件に一致するルームがありません
              </Alert>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someChecked && !allChecked}
                          checked={allChecked}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>No.</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>ルーム名</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>ステータス</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>顧客名</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>タグ</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>担当者</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }} align="right">メッセージ数</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>最終活動</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }} align="right">経過日数</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRooms.map((room) => {
                      const lastActivity = room.last_message_at || room.updated_at || room.created_at;
                      const days = daysSince(lastActivity);
                      const isSelected = selected.has(room.id);
                      const roomTags = getRoomAllTags(room);

                      return (
                        <TableRow
                          key={room.id}
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: 'pointer',
                            ...(days !== null && days >= 30 ? { bgcolor: 'action.hover' } : {}),
                          }}
                          onClick={() => handleSelectOne(room.id)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectOne(room.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {room.room_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={room.description || ''} placement="top">
                              <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                                {room.title}
                              </Typography>
                            </Tooltip>
                            {room.project_no && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                案件No: {room.project_no}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_LABELS[room.status] || room.status}
                              color={STATUS_COLORS[room.status] || 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {room.participant_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 220 }}>
                              {roomTags.length > 0 ? roomTags.map((tag, i) => (
                                <Chip
                                  key={i}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 22,
                                    borderColor: tag.includes(':') ? 'primary.light' : 'grey.400',
                                    color: tag.includes(':') ? 'primary.main' : 'text.secondary',
                                  }}
                                />
                              )) : (
                                <Typography variant="caption" color="text.disabled">-</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {room.created_by_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {room.message_count ?? 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontSize="0.8rem">
                              {formatDate(lastActivity)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {days !== null && (
                              <Chip
                                label={`${days}日`}
                                size="small"
                                color={days >= 30 ? 'error' : days >= 14 ? 'warning' : 'default'}
                                variant={days >= 14 ? 'filled' : 'outlined'}
                                sx={{ fontWeight: days >= 14 ? 'bold' : 'normal' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredRooms.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="表示件数"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}件`}
              />
            </>
          )}
        </Paper>

        {/* 一括操作確認ダイアログ */}
        <Dialog open={bulkDialogOpen} onClose={() => !processing && setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            一括ステータス変更の確認
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              この操作は取り消せません。対象ルームを十分に確認してください。
            </Alert>
            <Typography variant="body1" gutterBottom>
              <strong>{selected.size}件</strong>のルームを
              <Chip
                label={bulkAction === 'completed' ? '完了' : '削除'}
                color={bulkAction === 'completed' ? 'success' : 'error'}
                size="small"
                sx={{ mx: 0.5 }}
              />
              に変更します。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              各ルームにはシステムメッセージが記録されます。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDialogOpen(false)} disabled={processing}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              color={bulkAction === 'completed' ? 'success' : 'error'}
              onClick={handleBulkExecute}
              disabled={processing}
              startIcon={processing ? <CircularProgress size={16} /> : <SendIcon />}
            >
              {processing ? '処理中...' : `${selected.size}件を「${bulkAction === 'completed' ? '完了' : '削除'}」にする`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* スナックバー */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default RoomManagement;
